/**
 * Church Subscription Checkout entrypoint.
 * Loads the church + plan, then redirects to Stripe Checkout (subscription mode).
 *
 * Includes an in-page diagnostic panel that resolves the price_id lookup on
 * the backend whenever "Stripe price not configured" is encountered, so the
 * operator can see exactly which catalog row / Stripe key mode is failing.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CreditCard, ShieldCheck, ArrowRight, AlertTriangle, RefreshCw } from "lucide-react";

const VERDICT_HINT = {
  OK: "Lookup resolves — Continue to Stripe should work.",
  MISSING_SUBSCRIPTIONS_KEY: "STRIPE_SUBSCRIPTIONS_KEY is missing or set to the Emergent proxy in this environment. Add a real sk_test_… or sk_live_… key in Deployment → Environment Variables and redeploy.",
  EMPTY_CATALOG: "stripe_catalog is empty in this environment's MongoDB. Go to Super Admin → Stripe Catalog and click \"Flush & re-provision\".",
  MISSING_ROW: "Catalog has some rows but not this plan/interval combination. Run \"Flush & re-provision\" — or check Stripe dashboard for the matching live Price.",
  INVALID_PLAN: "Plan id is not one of starter/standard/premium.",
  INVALID_INTERVAL: "Interval must be 'monthly' or 'annual'.",
};

function DiagnosticPanel({ data, lastError, onRefresh, refreshing }) {
  if (!data && !lastError) return null;
  const v = data?.verdict;
  const ok = v === "OK";
  return (
    <div data-testid="cb-sub-diagnostic" className="mt-6 rounded-xl border border-red-500/40 bg-red-950/20 p-5 text-xs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-red-300">
          <AlertTriangle size={14} />
          <span className="font-semibold tracking-widest uppercase text-[10px]">Subscription diagnostic</span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          data-testid="cb-sub-diagnostic-refresh"
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-neutral-300 hover:text-white"
        >
          {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </div>
      {lastError && (
        <div className="mt-3 text-red-200">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Backend error</div>
          <div className="font-mono text-[11px] mt-1 break-words">{lastError}</div>
        </div>
      )}
      {data && (
        <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-neutral-300">
          <Row k="Requested plan" v={data.request?.plan_id} />
          <Row k="Requested interval" v={data.request?.interval} />
          <Row k="Lookup key" v={data.request?.lookup_key} mono />
          <Row k="Resolved price_id" v={data.catalog_row?.price_id || "— (not found)"} mono />
          <Row k="Catalog product_id" v={data.catalog_row?.product_id || "—"} mono />
          <Row k="Catalog amount" v={data.catalog_row?.unit_amount != null ? `${(data.catalog_row.unit_amount / 100).toFixed(2)} ${(data.catalog_row.currency || "gbp").toUpperCase()}` : "—"} />
          <Row k="Catalog rows" v={`${data.catalog_total_rows} of ${data.expected_rows}`} />
          <Row k="Catalog mode (meta)" v={data.catalog_meta?.mode || "—"} />
          <Row k="Stripe key mode (env)" v={data.stripe_key_mode} />
          <Row k="Church slug" v={data.church?.slug} mono />
          <Row k="Verdict" v={data.verdict} bold tone={ok ? "ok" : "bad"} />
        </div>
      )}
      {data && (
        <div className={`mt-4 text-[11px] rounded p-3 border ${ok ? "border-emerald-500/30 bg-emerald-900/10 text-emerald-200" : "border-amber-500/30 bg-amber-900/10 text-amber-200"}`}>
          {VERDICT_HINT[v] || "Unknown verdict."}
        </div>
      )}
    </div>
  );
}

function Row({ k, v, mono, bold, tone }) {
  const toneCls = tone === "ok" ? "text-emerald-300" : tone === "bad" ? "text-red-300" : "text-white";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1">
      <span className="text-[10px] tracking-widest uppercase text-neutral-500">{k}</span>
      <span className={`${mono ? "font-mono text-[11px]" : ""} ${bold ? "font-semibold" : ""} ${toneCls} truncate max-w-[60%] text-right`} title={String(v ?? "")}>{String(v ?? "—")}</span>
    </div>
  );
}

export default function ChurchSubscribe() {
  const { user, loading: authLoading } = useAuth();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [church, setChurch] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(params.get("plan") || "standard");
  const [interval, setInterval] = useState(params.get("interval") || "monthly");
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagError, setDiagError] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/church-builder/${slug}/subscribe`)}`); return; }
    (async () => {
      try {
        const [dash, pls] = await Promise.all([
          api.get(`/church-builder/by-slug/${slug}/dashboard`),
          api.get("/church-builder/plans"),
        ]);
        setChurch(dash.data.church);
        setPlans(pls.data);
      } catch (e) {
        toast.error("Could not load subscription page");
        navigate(`/church-builder/${slug}/dashboard`);
      } finally { setLoading(false); }
    })();
  }, [user, authLoading, slug, navigate]);

  const planDef = useMemo(() => plans.find((p) => p.id === planId), [plans, planId]);
  const price = planDef ? (interval === "annual" ? planDef.price_annual : planDef.price_monthly) : 0;

  const runDiagnostic = async (churchId, p, i) => {
    setDiagBusy(true);
    try {
      const { data } = await api.get(
        `/church-builder/churches/${churchId}/subscribe/preview`,
        { params: { plan_id: p, interval: i } }
      );
      setDiag(data);
    } catch (e) {
      // Even the diagnostic failed — surface that too
      setDiag({ verdict: "DIAGNOSTIC_FAILED", request: { plan_id: p, interval: i, lookup_key: `${p}_${i}` } });
      setDiagError((typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not load diagnostic"));
    } finally {
      setDiagBusy(false);
    }
  };

  const startCheckout = async () => {
    if (!church || !planDef) return;
    setRedirecting(true);
    setDiag(null);
    setDiagError(null);
    try {
      const { data } = await api.post(`/church-builder/churches/${church.id}/subscribe`, {
        plan_id: planId,
        interval,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setRedirecting(false);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Stripe checkout failed — is the API key configured?";
      toast.error(msg);
      setDiagError(msg);
      // Auto-fetch a lookup diagnostic so the operator can see why
      await runDiagnostic(church.id, planId, interval);
      setRedirecting(false);
    }
  };

  if (authLoading || loading || !church) {
    return <div className="max-w-7xl mx-auto px-6 py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  }

  return (
    <div data-testid="cb-subscribe" className="max-w-3xl mx-auto px-6 py-16 text-white">
      <div className="label-eyebrow mb-3">Subscribe</div>
      <h1 className="font-serif-display text-4xl">Activate <span className="italic text-[#D4AF37]">{church.name}</span></h1>
      <p className="text-neutral-300 mt-3 text-sm">7-day free trial — your card is collected upfront and only charged after the trial ends.</p>

      <div className="mt-10 grid sm:grid-cols-2 gap-3">
        {plans.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`cb-sub-pick-${p.id}`}
            onClick={() => setPlanId(p.id)}
            className={`text-left rounded-xl border p-5 transition-colors ${planId === p.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 bg-white/[0.02] hover:border-white/30"}`}
          >
            <div className="text-[10px] tracking-widest uppercase text-neutral-400">Plan</div>
            <div className="font-serif-display text-2xl mt-1">{p.name}</div>
            <div className="text-[#D4AF37] text-2xl mt-3 font-serif-display">£{interval === "annual" ? p.price_annual : p.price_monthly}<span className="text-xs text-neutral-500">{interval === "annual" ? "/yr" : "/mo"}</span></div>
            <div className="text-xs text-neutral-400 mt-2">{p.tagline}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 inline-flex border border-white/10 rounded-full p-1">
        {["monthly", "annual"].map((b) => (
          <button key={b} data-testid={`cb-sub-billing-${b}`} onClick={() => setInterval(b)}
            className={`px-4 py-1.5 text-[10px] tracking-widest uppercase rounded-full ${interval === b ? "bg-[#D4AF37] text-black" : "text-neutral-400"}`}>
            {b}
          </button>
        ))}
      </div>

      <div className="mt-10 luxury-card rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-serif-display text-2xl">{planDef?.name || "—"}</div>
            <div className="text-xs text-neutral-400 mt-1">7-day free trial, then £{price}{interval === "annual" ? "/yr" : "/mo"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500">Charged today</div>
            <div className="font-serif-display text-3xl text-[#D4AF37]">£0.00</div>
          </div>
        </div>
        <ul className="mt-5 space-y-2 text-xs text-neutral-400">
          <li className="flex items-center gap-2"><ShieldCheck size={13} className="text-[#D4AF37]" /> Cancel any time from your dashboard</li>
          <li className="flex items-center gap-2"><CreditCard size={13} className="text-[#D4AF37]" /> Card collected by Stripe — we never see it</li>
        </ul>
        <button
          type="button"
          data-testid="cb-sub-checkout"
          onClick={startCheckout}
          disabled={redirecting}
          className="btn-gold mt-6 w-full px-6 py-3 rounded text-xs tracking-widest uppercase font-semibold inline-flex justify-center items-center gap-2"
        >
          {redirecting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Continue to Stripe
        </button>

        <div className="mt-3 flex items-center justify-between text-[10px] tracking-widest uppercase text-neutral-500">
          <Link to={`/church-builder/${slug}/dashboard`} data-testid="cb-sub-skip" className="hover:text-neutral-300">Skip — continue on free trial</Link>
          <button
            type="button"
            data-testid="cb-sub-run-diag"
            onClick={() => runDiagnostic(church.id, planId, interval)}
            disabled={diagBusy}
            className="hover:text-neutral-300 inline-flex items-center gap-1"
          >
            {diagBusy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Run diagnostic
          </button>
        </div>
      </div>

      <DiagnosticPanel
        data={diag}
        lastError={diagError}
        onRefresh={() => runDiagnostic(church.id, planId, interval)}
        refreshing={diagBusy}
      />
    </div>
  );
}
